<script setup>
import {getCurrentInstance, onMounted, ref} from 'vue'
import helpers from '@/lib/helpers'

const {proxy} = getCurrentInstance()

const props = defineProps({
  rule: {type: Object, required: true},
})
const emit = defineEmits(['back'])

const counters = ref(null)
const recent = ref([])
const err = ref('')

async function load() {
  try {
    const r = await helpers.asyncAction(props.rule._links.recent_url.href, 'GET', {noload: 'nrules', quiet: true})
    counters.value = r.counters
    recent.value = r.recent || []
    err.value = ''
  } catch (e) {
    err.value = e.message || 'Could not load activity'
  }
}

function open(n) {
  if (n.data && n.data.gli) proxy.global_lookup(n.data.gli, true)
}

onMounted(load)
</script>

<template>
  <div class="d-flex flex-column h-100">
    <div class="p-2 border-bottom d-flex align-items-center">
      <b-button variant="link" size="sm" class="text-secondary px-0" @click="emit('back')">
        <font-awesome-icon :icon="['fas', 'arrow-left']"/> Back to rule
      </b-button>
      <span class="ml-2 font-weight-bold">{{ rule.name || rule.trigger_label }}</span>
    </div>

    <div class="flex-fill overflow-auto p-3">
      <b-alert :show="!!err" variant="danger">{{ err }}</b-alert>

      <b-alert :show="!!(counters && counters.error)" variant="warning" class="small">
        <font-awesome-icon :icon="['fas', 'triangle-exclamation']"/>
        Last run errored: <code>{{ counters && counters.error }}</code>
      </b-alert>

      <b-row v-if="counters" class="text-center mb-3">
        <b-col class="stat">
          <div class="h4 mb-0 text-primary">{{ counters.match_count }}</div>
          <div class="text-muted small text-uppercase">Delivered</div>
        </b-col>
        <b-col class="stat border-left border-right">
          <div class="h4 mb-0">{{ counters.fire_count }}</div>
          <div class="text-muted small text-uppercase">Times fired</div>
        </b-col>
        <b-col class="stat">
          <div class="h6 mb-0">{{ (counters.last_fired_at | date) || '—' }}</div>
          <div class="text-muted small text-uppercase">Last</div>
        </b-col>
      </b-row>

      <h6 class="text-muted text-uppercase small">Recent notifications</h6>
      <div v-if="!recent.length" class="text-muted small">Nothing delivered yet.</div>
      <b-list-group v-else flush>
        <b-list-group-item
          v-for="n in recent"
          :key="n.id"
          button
          class="py-2"
          @click="open(n)">
          <div class="d-flex">
            <div class="flex-fill min-w-0">
              <div class="font-weight-bold text-truncate">{{ n.title }}</div>
              <div class="text-muted small text-truncate">{{ n.body }}</div>
            </div>
            <div class="text-muted small text-nowrap ml-2">{{ n.created | timediff }}</div>
          </div>
        </b-list-group-item>
      </b-list-group>
    </div>
  </div>
</template>

<style scoped>
.stat {
  padding: .5rem 0;
}

.min-w-0 {
  min-width: 0;
}
</style>
